import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Spinner,
  Text,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Tag,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import xhr from "xhr";
import dayjs from "dayjs";
import SchoolBadge from "components/SchoolBadge";

export default function TomarAsistencia({ liveClass, onClose }) {
  const toast = useToast();

  const [fetching, setFetching] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [absenceByStudentId, setAbsenceByStudentId] = useState({});
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [loadingSubjectEnrollments, setLoadingSubjectEnrollments] = useState(false);
  const [subjectEnrollmentByStudentId, setSubjectEnrollmentByStudentId] = useState({});

  const [savingByStudentId, setSavingByStudentId] = useState({});

  const [loadingExcuses, setLoadingExcuses] = useState(false);
  const [excusedStudentIds, setExcusedStudentIds] = useState({});

  const [loadingCameraExcuses, setLoadingCameraExcuses] = useState(false);
  const [cameraExcusedStudentIds, setCameraExcusedStudentIds] = useState({});

  const groupId = liveClass?.class_group?.id || "";
  const subjectId = liveClass?.subject?.id || "";

  // Segmentos de la clase: el programa principal y, si es compartida, cada
  // materia adicional con su programa. Cada estudiante queda asociado a su
  // segmento para registrar la asistencia con SU grupo y SU programa.
  const segments = useMemo(() => {
    const programOf = (entity) => {
      const field = ["grade", "course", "technical_career"].find((k) => entity?.[k]?.id);
      if (!field) return null;
      const pt = entity[field].program_type;
      return {
        field,
        id: entity[field].id,
        programTypeId: pt?.id || pt || "",
        programTypeSlug: pt?.slug || pt?.name || "",
      };
    };

    const list = [];
    const main = programOf(liveClass);
    if (main && subjectId) {
      list.push({ ...main, subjectId, semester: liveClass?.semester || "", groupIds: groupId ? [groupId] : [""] });
    }

    // Grupos adicionales acotan solo si se marcaron para ese programa;
    // si no, se toman todos los grupos activos del programa.
    (liveClass?.other_subjects || []).forEach((s) => {
      const p = programOf(s);
      if (!p) return;
      const groupIds = (liveClass?.other_class_groups || [])
        .filter((g) => g?.[p.field]?.id === p.id)
        .map((g) => g.id);
      list.push({ ...p, subjectId: s.id, semester: "", groupIds, allGroups: groupIds.length === 0 });
    });

    return list;
  }, [liveClass, subjectId, groupId]);

  const { isOpen, onOpen, onClose: onCloseModal } = useDisclosure();
  const [selectedDisease, setSelectedDisease] = useState(null);

  const openDiseaseModal = (disease, student) => {
    setSelectedDisease({
      disease,
      student,
    });
    onOpen();
  };

  // cargar estudiantes usando el endpoint del admin
  useEffect(() => {
    const run = async () => {
      if (!liveClass?.id) return;

      setFetching(true);
      try {
        const all = [];

        for (const seg of segments) {
          let groups = seg.groupIds;
          if (seg.allGroups) {
            const resGroups = await xhr(
              `/class-groups?filters[${seg.field}][id][$eq]=${seg.id}&filters[active][$eq]=true&fields[0]=id&pagination[limit]=-1`
            );
            groups = (resGroups?.data?.data || []).map((el) => el.id);
          }

          for (const gid of groups) {
            const qs = new URLSearchParams({
              status: "estudiando",
              include_promotion_pending: "true",
              semester: seg.semester,
              program: String(seg.id),
              program_type: seg.programTypeSlug || "",
              group: gid ? String(gid) : "",
              page: "1",
              pageSize: "999999",
            });

            const url = `/students/generalFilter?${qs.toString()}`;
            const res = await xhr(url);
            (res?.data?.students || []).forEach((r) => all.push({ ...r, _segment: { ...seg, groupId: gid } }));
          }
        }

        setRows(all);
      } catch (e) {
        console.error("Error cargando estudiantes (generalFilter):", e);
        setRows([]);
      } finally {
        setFetching(false);
      }
    };

    run();
  }, [liveClass?.id, segments]);

  // deduplicar estudiantes
  const students = useMemo(() => {
    const map = new Map();

    (rows || []).forEach((r) => {
      const st = r?.student || {};
      const u = st?.user || {};
      const fullName = `${u?.first_name || ""} ${u?.last_name || ""}`.trim();
      if (!st?.id || !fullName) return;

      const desease =
        st?.desease ||
        st?.attributes?.desease ||
        r?.desease ||
        r?.attributes?.desease ||
        null;

      const school = st?.school ?? st?.attributes?.school ?? null;
      const schoolFrom = st?.schoolFrom ?? st?.attributes?.schoolFrom ?? null;

      if (!map.has(st.id))
        map.set(st.id, { id: st.id, name: fullName, desease, school, schoolFrom, segment: r._segment });
    });

    return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
  }, [rows]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) => String(s.id).includes(q) || (s.name || "").toLowerCase().includes(q)
    );
  }, [students, search]);

  // cargar inasistencias ya registradas para esta live_class
  useEffect(() => {
    const run = async () => {
      if (!liveClass?.id) {
        setAbsenceByStudentId({});
        return;
      }

      setLoadingAbsences(true);
      try {
        const res = await xhr(
          `/student-attendances?filters[live_class][id][$eq]=${liveClass.id}&pagination[limit]=-1&populate[student]=*`
        );

        const list = res?.data?.data || [];
        const map = {};

        list.forEach((item) => {
          const studentId =
            item?.student?.id ||
            item?.attributes?.student?.data?.id ||
            item?.attributes?.student?.id;

          const absenceId = item?.id || item?.attributes?.id;

          if (studentId && absenceId) {
            map[Number(studentId)] = Number(absenceId);
          }
        });

        setAbsenceByStudentId(map);
      } catch (e) {
        console.error("Error cargando inasistencias de la clase:", e);
        setAbsenceByStudentId({});
      } finally {
        setLoadingAbsences(false);
      }
    };

    run();
  }, [liveClass?.id]);

  // excusas normales por live_class
  useEffect(() => {
    const run = async () => {
      if (!liveClass?.id) {
        setExcusedStudentIds({});
        return;
      }

      setLoadingExcuses(true);
      try {
        const res = await xhr(
          `/student-excuses?filters[live_classes][id][$eq]=${Number(liveClass.id)}` +
          `&pagination[limit]=-1&populate[student]=*`
        );

        const list = res?.data?.data || [];
        const map = {};

        list.forEach((item) => {
          const a = item?.attributes || item;

          const stId =
            item?.student?.id ||
            a?.student?.data?.id ||
            a?.student?.id;

          if (!stId) return;

          map[Number(stId)] = true;
        });

        setExcusedStudentIds(map);
      } catch (e) {
        console.error("Error cargando excusas por live_class:", e);
        setExcusedStudentIds({});
      } finally {
        setLoadingExcuses(false);
      }
    };

    run();
  }, [liveClass?.id]);

  // excusas por cámara por live_class
  useEffect(() => {
    const run = async () => {
      if (!liveClass?.id) {
        setCameraExcusedStudentIds({});
        return;
      }

      setLoadingCameraExcuses(true);
      try {
        const res = await xhr(
          `/student-excuse-cameras?filters[live_classes][id][$eq]=${Number(liveClass.id)}` +
          `&pagination[limit]=-1&populate[student]=*`
        );

        const list = res?.data?.data || [];
        const map = {};

        list.forEach((item) => {
          const a = item?.attributes || item;

          const stId =
            item?.student?.id ||
            a?.student?.data?.id ||
            a?.student?.id;

          if (!stId) return;

          map[Number(stId)] = true;
        });

        setCameraExcusedStudentIds(map);
      } catch (e) {
        console.error("Error cargando excusas por cámara por live_class:", e);
        setCameraExcusedStudentIds({});
      } finally {
        setLoadingCameraExcuses(false);
      }
    };

    run();
  }, [liveClass?.id]);

  // matrícula (subject-enrollment) de cada estudiante en la materia de SU segmento
  useEffect(() => {
    const run = async () => {
      if (!liveClass?.id || segments.length === 0 || students.length === 0) {
        setSubjectEnrollmentByStudentId({});
        return;
      }

      setLoadingSubjectEnrollments(true);

      try {
        const map = {};

        for (const seg of segments) {
          const url =
            `/subject-enrollments?` +
            `filters[subject][id][$eq]=${Number(seg.subjectId)}&` +
            `filters[${seg.field}][id][$eq]=${Number(seg.id)}&` +
            `pagination[limit]=-1&` +
            `populate=student`;

          const res = await xhr(url);

          (res?.data?.data || []).forEach((item) => {
            const studentId =
              item?.student?.id ||
              item?.student?.data?.id ||
              item?.attributes?.student?.data?.id ||
              item?.attributes?.student?.id;

            const enrollmentId = item?.id || item?.attributes?.id;

            if (!studentId || !enrollmentId) return;

            map[Number(studentId)] = {
              id: enrollmentId,
              camera_percentage: item?.camera_percentage ?? item?.attributes?.camera_percentage ?? 100,
            };
          });
        }

        setSubjectEnrollmentByStudentId(map);
      } catch (e) {
        console.error("Error cargando subject-enrollments:", e);
        setSubjectEnrollmentByStudentId({});
      } finally {
        setLoadingSubjectEnrollments(false);
      }
    };

    run();
  }, [liveClass?.id, segments, students]);

  const isStudentExcused = useCallback(
    (studentId) => !!excusedStudentIds?.[Number(studentId)],
    [excusedStudentIds]
  );

  const isStudentCameraExcused = useCallback(
    (studentId) => !!cameraExcusedStudentIds?.[Number(studentId)],
    [cameraExcusedStudentIds]
  );

  // registrar inasistencia
  const createAbsence = async (student) => {
    if (!student?.id) return;

    if (isStudentExcused(student.id)) {
      toast({
        title: "Estudiante excusado",
        description: "Este estudiante tiene una excusa para esta clase.",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    // La asistencia se registra con el grupo y el programa DEL ESTUDIANTE
    // (en una clase compartida puede ser de otro programa).
    const seg = student.segment || {};

    if (!liveClass?.id || !seg.groupId || !seg.id || !seg.programTypeId) {
      toast({
        title: "Faltan datos",
        description: "No se encontró live_class, class_group, programa o program_type.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setSavingByStudentId((prev) => ({ ...prev, [student.id]: true }));

    try {
      const payloadData = {
        student: Number(student.id),
        class_group: Number(seg.groupId),
        program_type: Number(seg.programTypeId),
        live_class: Number(liveClass.id),
        [seg.field]: Number(seg.id),
      };

      const payload = {
        data: payloadData,
      };

      const res = await xhr.post("/student-attendances", payload);

      const newId = res?.data?.data?.id || res?.data?.id;

      setAbsenceByStudentId((prev) => ({
        ...prev,
        [Number(student.id)]: Number(newId),
      }));

      toast({
        title: "Inasistencia registrada",
        description: `${student.name} (${student.id})`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      console.error("Error creando inasistencia:", e);
      toast({
        title: "Error registrando inasistencia",
        description: "Puede ser duplicado o falta un campo requerido.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingByStudentId((prev) => ({ ...prev, [student.id]: false }));
    }
  };

  const deleteAbsence = async (student) => {
    const absenceId = absenceByStudentId?.[Number(student.id)];
    if (!absenceId) return;

    // Si está excusado NO permitir borrar desde teacher
    if (isStudentExcused(student.id)) return;

    const ok = window.confirm(`¿Borrar inasistencia de ${student.name}?`);
    if (!ok) return;

    setSavingByStudentId((prev) => ({ ...prev, [student.id]: true }));

    try {
      await xhr(`/student-attendances/${absenceId}`, { method: "DELETE" });

      setAbsenceByStudentId((prev) => {
        const copy = { ...prev };
        delete copy[Number(student.id)];
        return copy;
      });

      toast({
        title: "Inasistencia borrada",
        description: `${student.name} (${student.id})`,
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      console.error("Error borrando inasistencia:", e);
      toast({
        title: "Error borrando inasistencia",
        description: "Revisa permisos del role o que el ID exista.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingByStudentId((prev) => ({ ...prev, [student.id]: false }));
    }
  };

  const isBusy =
    fetching || loadingAbsences || loadingExcuses || loadingCameraExcuses || loadingSubjectEnrollments;

  const decreaseCameraPercentage = async (student) => {
    if (!student?.id) return;

    if (isStudentCameraExcused(student.id)) {
      toast({
        title: "Estudiante excusado por cámara",
        description: "No se puede bajar la nota de cámara porque tiene excusa.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const enrollment = subjectEnrollmentByStudentId?.[Number(student.id)];

    if (!enrollment?.id) {
      toast({
        title: "No se encontró matrícula",
        description: `No se encontró subject-enrrolment para ${student.name}.`,
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const ok = window.confirm(`¿Bajar 10% la nota de cámara de ${student.name}?`);
    if (!ok) return;

    setSavingByStudentId((prev) => ({ ...prev, [student.id]: true }));

    try {
      const currentPercentage = enrollment?.camera_percentage ?? 100;
      const newPercentage = Math.max(Number(currentPercentage) - 10, 0);

      await xhr.put(`/subject-enrollments/${enrollment.id}`, {
        data: {
          camera_percentage: newPercentage,
        },
      });

      setSubjectEnrollmentByStudentId((prev) => ({
        ...prev,
        [Number(student.id)]: {
          ...prev[Number(student.id)],
          camera_percentage: newPercentage,
        },
      }));

      toast({
        title: "Nota de cámara actualizada",
        description: `${student.name}: ${currentPercentage}% → ${newPercentage}%`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (e) {
      console.error("Error bajando nota de cámara:", e);

      toast({
        title: "Error",
        description: "No se pudo actualizar la nota de cámara.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSavingByStudentId((prev) => ({ ...prev, [student.id]: false }));
    }
  };

  return (
    <Box mt={5} border="1px solid" borderColor="gray.200" rounded="md" p={3}>
      <Flex align="center" justify="space-between" mb={3}>
        <Box>
          <Text fontSize="18px" fontWeight="bold">
            Tomar asistencia
          </Text>
          <Text fontSize="13px" color="gray.600">
            Clase: <b>{liveClass?.title || "—"}</b> — Grupo:{" "}
            <b>{liveClass?.class_group?.name || liveClass?.class_group?.id || "—"}</b>
          </Text>
        </Box>

        <Button size="sm" variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </Flex>

      <Flex gap={3} wrap="wrap" mb={3}>
        <Input
          width="260px"
          placeholder="Buscar por código o nombre"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          isDisabled={isBusy}
        />
        <Button onClick={() => setSearch("")} isDisabled={!search}>
          Limpiar
        </Button>

        {isBusy && (
          <Flex align="center" gap={2}>
            <Spinner size="sm" />
            <Text fontSize="13px">
              {fetching
                ? "Cargando estudiantes..."
                : loadingAbsences
                  ? "Cargando inasistencias..."
                  : loadingExcuses
                    ? "Cargando excusas..."
                    : loadingCameraExcuses
                      ? "Cargando excusas por cámara..."
                      : "Cargando porcentaje de cámara..."}
            </Text>
          </Flex>
        )}
      </Flex>

      <Box mb={2} fontSize="14px" color="gray.700">
        Total: <b>{filteredStudents.length}</b>
      </Box>

      <TableContainer border="1px solid" borderColor="gray.200" rounded="md">
        <Table size="sm" variant="simple">
          <Thead bg="gray.100">
            <Tr>
              <Th width="140px">CÓDIGO</Th>
              <Th>ESTUDIANTE</Th>
              <Th width="260px">INASISTENCIA</Th>
              <Th width="130px">EXCUSA CAM</Th>
              <Th width="150px">NOTA CAM</Th>
              <Th width="170px">BAJAR CÁMARA</Th>
              <Th width="180px">CARACTERÍSTICAS</Th>
            </Tr>
          </Thead>

          <Tbody>
            {isBusy ? (
              <Tr>
                <Td colSpan={7}>
                  <Flex align="center" justify="center" p={6}>
                    <Spinner />
                  </Flex>
                </Td>
              </Tr>
            ) : filteredStudents.length === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  <Box p={3}>No hay estudiantes con esos filtros.</Box>
                </Td>
              </Tr>
            ) : (
              filteredStudents.map((s) => {
                const hasAbsence = !!absenceByStudentId?.[Number(s.id)];
                const isSaving = !!savingByStudentId?.[Number(s.id)];
                const excused = isStudentExcused(s.id);
                const cameraExcused = isStudentCameraExcused(s.id);

                return (
                  <Tr key={s.id}>
                    <Td>{s.id}</Td>
                    <Td>
                      {s.name}
                      <SchoolBadge student={s} />
                      {excused ? (
                        <Tag ml={2} size="sm" colorScheme="green">
                          Excusa
                        </Tag>
                      ) : null}
                    </Td>
                    <Td>
                      {hasAbsence ? (
                        excused || cameraExcused ? (
                          <Button size="xs" isDisabled colorScheme="gray">
                            Excusada
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            colorScheme="gray"
                            onClick={() => deleteAbsence(s)}
                            isLoading={isSaving}
                          >
                            Borrar inasistencia
                          </Button>
                        )
                      ) : (
                        <Button
                          size="xs"
                          colorScheme="red"
                          onClick={() => createAbsence(s)}
                          isLoading={isSaving}
                          isDisabled={excused}
                        >
                          Registrar inasistencia
                        </Button>
                      )}
                    </Td>
                    <Td>
                      {cameraExcused ? (
                        <Tag size="sm" colorScheme="purple">
                          Sí
                        </Tag>
                      ) : (
                        <Tag size="sm" colorScheme="gray">
                          No
                        </Tag>
                      )}
                    </Td>

                    <Td>
                      <Text fontSize="13px" fontWeight="bold">
                        {subjectEnrollmentByStudentId?.[Number(s.id)]?.camera_percentage ?? 100}%
                      </Text>
                    </Td>

                    <Td>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        onClick={() => decreaseCameraPercentage(s)}
                        isLoading={isSaving}
                        isDisabled={
                          cameraExcused ||
                          !subjectEnrollmentByStudentId?.[Number(s.id)]?.id
                        }
                      >
                        -10%
                      </Button>
                    </Td>

                    <Td>
                      {s?.desease ? (
                        <Button
                          size="xs"
                          colorScheme="blue"
                          onClick={() => openDiseaseModal(s.desease, s)}
                        >
                          Características
                        </Button>
                      ) : (
                        <Text fontSize="12px" color="gray.400">
                          —
                        </Text>
                      )}
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal isOpen={isOpen} onClose={onCloseModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Características del estudiante</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontWeight="bold" mb={2}>
              {selectedDisease?.student?.name} ({selectedDisease?.student?.id})
            </Text>
            <Text whiteSpace="pre-wrap">
              {selectedDisease?.disease || "No hay información."}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseModal}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}